import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  withStyles,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';
import { AgGrid } from '../../AgGrid';
import { getAllMfaManagementUsers, bulkUpdateMfaUsers } from '../../../services/mfaManagement.service';
import ErrorMessage from '../../Error/ErrorMessage';

const styles = theme => ({});

const TwoFactorEnabledCellRenderer = (props) => {
  const value = props.value;
  
  if (value === undefined || value === null) {
    return <span>-</span>;
  }
  
  if (value === true || value === 'true' || value === 1) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Enabled" placement="top">
          <CheckCircleIcon style={{ color: '#4caf50', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip title="Disabled" placement="top">
        <CancelIcon style={{ color: '#f44336', fontSize: 24 }} />
      </Tooltip>
    </div>
  );
};

export const MfaManagementUsersGrid = withRouter(withStyles(styles)(props => {
  const [gridData, setGridData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditRowData, setBulkEditRowData] = useState([]); // Array of { userId, username }
  const [selectedAction, setSelectedAction] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [bulkUpdateResults, setBulkUpdateResults] = useState(null);

  const columnDefinitions = [
    { 
      headerName: '', 
      field: 'checkbox',
      width: 50,
      checkboxSelection: true,
      suppressMenu: true,
      resizable: false,
      sortable: false,
      filter: false,
    },
    { 
      headerName: 'Username', 
      field: 'username', 
      width: 200,
      resizable: true,
      valueGetter: params => params?.data?.username || params?.data?.Username 
    },
    { 
      headerName: 'E-mail', 
      field: 'email', 
      width: 250,
      resizable: true,
      valueGetter: params => params?.data?.email || params?.data?.Email 
    },
    { 
      headerName: 'Phone Number', 
      field: 'phoneNumber',
      width: 200,
      resizable: true,
      valueGetter: params => params?.data?.phoneNumber || params?.data?.PhoneNumber 
    },
    { 
      headerName: 'Role', 
      field: 'roleName',
      width: 150,
      resizable: true,
      valueGetter: params => params?.data?.roleName || params?.data?.RoleName 
    },
    { 
      headerName: 'Two Factor Enabled', 
      field: 'twoFactorEnabled',
      width: 175,
      resizable: true,
      cellRenderer: 'twoFactorEnabledCellRenderer',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [true, false],
        valueFormatter: (params) => params.value ? 'Enabled' : 'Disabled',
        cellRenderer: 'twoFactorEnabledCellRenderer'
      },
      valueGetter: params => params?.data?.twoFactorEnabled ?? params?.data?.TwoFactorEnabled 
    },
    { 
      headerName: 'ID', 
      field: 'userId', 
      hide: true, 
      valueGetter: params => params?.data?.userId || params?.data?.UserId 
    },
  ];

  const gridOptions = {
    suppressColumnVirtualisation: true,
    defaultColDef: {
      resizable: true,
      suppressAutoSize: false,
      flex: 1
    },
    frameworkComponents: {
      twoFactorEnabledCellRenderer: TwoFactorEnabledCellRenderer,
    },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [10, 25, 50, 100],
    suppressPaginationPanel: false,
    rowSelection: 'multiple',
  };

  const openBulkEditDialog = rowData => {
    const users = rowData.map(row => ({
      userId: row.userId || row.UserId,
      username: row.username || row.Username
    })).filter(user => user.userId);
    console.log('Bulk edit selected users:', users);
    setBulkEditRowData(users);
    setShowBulkEditDialog(true);
  };

  const handleBulkEditDialogClose = (shouldRefetch) => {
    setShowBulkEditDialog(false);
    setBulkEditRowData([]);
    setSelectedAction('');
    setIsSubmitting(false);
    setSubmitMessage(null);
    setBulkUpdateResults(null);
    
    if (shouldRefetch) {
      loadUsers();
    }
  };

  const handleBulkActionSubmit = async () => {
    // If results are already shown, close and refresh
    if (bulkUpdateResults) {
      return handleBulkEditDialogClose(true);
    }

    if (!selectedAction) {
      setSubmitMessage('Please select an action');
      setTimeout(() => setSubmitMessage(null), 3000);
      return;
    }

    const twoFactorEnabled = selectedAction === 'enable';

    try {
      setIsSubmitting(true);
      setSubmitMessage(null);
      // Extract just the userIds for the API call
      const userIds = bulkEditRowData.map(user => user.userId);
      const result = await bulkUpdateMfaUsers(userIds, twoFactorEnabled);
      
      setIsSubmitting(false);
      setBulkUpdateResults(result);
      setSubmitMessage(`Successfully updated ${result.successCount} of ${result.totalProcessed} user(s)`);
    } catch (error) {
      setIsSubmitting(false);
      setSubmitMessage(error.message || 'Failed to update users');
    }
  };

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getAllMfaManagementUsers();
      setGridData(data || []);
      setIsError(false);
    } catch (err) {
      setIsError(true);
      setGridData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return <ErrorMessage message="There was an error fetching MFA users." />;
  }

  return (
    <div className={classnames('flex-1', 'card', 'border-left-green')}>
      <AgGrid
        gridData={gridData}
        gridOptions={gridOptions}
        columnDefs={columnDefinitions}
        showCreateButton={false}
        redirectOnClick={true}
        editOnClick={false}
        handleCellClick={evt => {
          const userId = evt?.data?.userId || evt?.data?.UserId;
          console.log('Clicked user ID:', userId, 'Full data:', evt.data);
          if (userId) {
            props.history.push(`/user-management/mfa-management/${userId}`);
          } else {
            console.error('No user ID found in row data:', evt.data);
          }
        }}
        openBulkEditDialog={openBulkEditDialog}
        rowClassRules={{}}
      />

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onClose={() => handleBulkEditDialogClose(false)} aria-labelledby="bulk-dialog-title">
        <DialogTitle id="bulk-dialog-title">Bulk Edit MFA Settings</DialogTitle>
        <DialogContent
          dividers
          style={{
            minWidth: 500,
            minHeight: 250,
            borderTop: '0.8px solid #ddd',
            borderBottom: '0.8px solid #ddd',
            paddingTop: 20,
            pointerEvents: isSubmitting ? 'none' : 'auto',
            opacity: isSubmitting ? 0.5 : 1,
          }}
        >
          {!bulkUpdateResults ? (
            <>
              <div>Selected Users:</div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: 3,
                  marginBottom: 20,
                  minHeight: 25,
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: !bulkEditRowData || !bulkEditRowData.length ? '1px solid #999' : '1px solid black',
                  borderRadius: 3,
                  padding: 10,
                }}
              >
                {bulkEditRowData && !!bulkEditRowData.length ? (
                  bulkEditRowData.map((user, index) => (
                    <div key={user.userId} style={{ padding: '3px 0' }}>
                      {user.username}
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#999' }}>No users were selected</div>
                )}
              </div>

              <FormControl fullWidth variant="outlined">
                <InputLabel id="bulk-action-label">Select Action</InputLabel>
                <Select
                  labelId="bulk-action-label"
                  value={selectedAction}
                  onChange={e => setSelectedAction(e.target.value)}
                  label="Select Action"
                  disabled={isSubmitting}
                >
                  <MenuItem value="">
                    <em>Select an action</em>
                  </MenuItem>
                  <MenuItem value="enable">Enable Two Factor</MenuItem>
                  <MenuItem value="disable">Disable Two Factor</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : (
            <div className="col-start-center">
              <h4>Update Results</h4>
              <div style={{ marginTop: 10, marginBottom: 15 }}>
                <div><strong>Total Processed:</strong> {bulkUpdateResults.totalProcessed}</div>
                <div style={{ color: '#4caf50' }}><strong>Success:</strong> {bulkUpdateResults.successCount}</div>
                {bulkUpdateResults.failCount > 0 && (
                  <div style={{ color: '#f44336' }}><strong>Failed:</strong> {bulkUpdateResults.failCount}</div>
                )}
              </div>
              {bulkUpdateResults.results && bulkUpdateResults.results.length > 0 && (
                <div style={{ width: '100%', maxHeight: 300, overflowY: 'auto' }}>
                  {bulkUpdateResults.results.map((result, index) => (
                    <div key={index} className="row-center-center full-width mt-5" style={{ borderBottom: '1px solid #eee', paddingBottom: 5 }}>
                      <div className="flex-1 text-right mr-15">{result.username || result.Username}</div>
                      <div
                        className="flex-1 text-left ml-15"
                        style={{
                          color: result.success || result.Success ? '#4caf50' : '#f44336',
                          fontWeight: 'bold',
                        }}
                      >
                        {result.success || result.Success ? (
                          '✓ Success'
                        ) : (
                          <span>✗ {result.message || result.Message || 'Failed'}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Button
            onClick={() => handleBulkEditDialogClose(false)}
            variant="contained"
            style={{ backgroundColor: '#315382', color: 'white' }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <div style={{ flex: 1, paddingLeft: 10, paddingRight: 10 }}>
            {isSubmitting && <LinearProgress />}
            {submitMessage && <div className="text-center">{submitMessage}</div>}
          </div>
          <Button
            disabled={isSubmitting || (!selectedAction && !bulkUpdateResults)}
            onClick={handleBulkActionSubmit}
            variant="contained"
            style={{ backgroundColor: '#315382', color: 'white' }}
          >
            {bulkUpdateResults ? 'Done' : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}));

